import React, { Component } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import { FolderWrapper } from 'dibs-portfolio-folder/exports/FolderWrapper';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';

import asyncPortfolioModalLoader from '../sharedComponents/asyncPortfolioModalLoader';

class FolderWrapperWrapper extends Component {
    constructor(props) {
        super(props);

        this.addItemSuccess = this.addItemSuccess.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.showAuthModal = this.showAuthModal.bind(this);
        this.getModalComponent = this.getModalComponent.bind(this);
    }

    getModalComponent() {
        return new Promise((resolve, reject) => {
            const loadModal = () => {
                asyncPortfolioModalLoader.loadPortfolioModal(AddToFolderModal =>
                    AddToFolderModal ? resolve(AddToFolderModal) : reject()
                );
            };
            if (!this.props.userId) {
                return this.showAuthModal().then(({ err }) => {
                    if (err) {
                        return reject(`AuthModal - ${err}`);
                    }
                    return loadModal();
                });
            } else {
                return loadModal();
            }
        });
    }

    showAuthModal() {
        return new Promise(resolve => {
            this.props.onAuthModalShow();
            authModalLoader
                .show({
                    action: authModalLoader.constants.LOGIN,
                    flow: authModalLoader.constants.SAVE_ITEM_FLOW,
                    ga: {
                        label: this.props.authModalTrackingLabel,
                    },
                    shouldPauseAuthFlow: state => state.step === 0,
                })
                .then(resolve);
        });
    }

    closeModal() {
        if (this.props.closeModal) {
            this.props.closeModal();
        }
    }

    addItemSuccess(payload) {
        if (authModalLoader.isPaused()) {
            authModalLoader.resume().then(() => this.props.onAddItemSuccess(payload));
        } else {
            this.props.onAddItemSuccess(payload);
        }
    }

    render() {
        return (
            <FolderWrapper
                additionalIconClass={this.props.additionalIconClass}
                allowSavingMultipleItems={this.props.allowSavingMultipleItems}
                closeModal={this.closeModal}
                currentPortfolioId={this.props.currentPortfolioId}
                disabledButton={this.props.disabledButton}
                environment={this.props.relay.environment}
                getModalComponent={this.getModalComponent}
                iconPosition={this.props.iconPosition}
                iconText={this.props.iconText}
                loadPortfolioItemData={this.props.loadPortfolioItemData}
                modalHeaderText={this.props.modalHeaderText}
                onAddItemError={this.props.onAddItemError}
                onAddItemSuccess={this.addItemSuccess}
                onAddNoteError={this.props.onAddNoteError}
                onAddNoteSuccess={this.props.onAddNoteSuccess}
                onCreateFolderError={this.props.onCreateFolderError}
                onCreateFolderSuccess={this.props.onCreateFolderSuccess}
                onFolderClick={this.loadModal}
                onFolderRemove={this.props.onFolderRemove}
                onFolderSave={this.props.onFolderSave}
                onNewFolderSave={this.props.onNewFolderSave}
                onRemoveItemError={this.props.onRemoveItemError}
                onRemoveItemSuccess={this.props.onRemoveItemSuccess}
                primaryItemPk={this.props.primaryItemPk}
                shouldFillInFolder={this.props.shouldFillInFolder}
                singlePortfolioItem={this.props.singlePortfolioItem}
                selectedItemIds={this.props.selectedItemIds}
                selectedItemImages={this.props.selectedItemImages}
                selectedItemTitles={this.props.selectedItemTitles}
                trackingHooks={this.props.trackingHooks}
                userId={this.props.userId}
                userIds={this.props.userIds}
                viewer={this.props.viewer}
            />
        );
    }
}

// ignore default prop functions from coverage stats
/* istanbul ignore next */
FolderWrapperWrapper.defaultProps = {
    additionalIconClass: '',
    allowSavingMultipleItems: false,
    authModalTrackingLabel: 'save item',
    currentPortfolioId: '',
    disabledButton: false,
    iconPosition: 'absolute',
    iconText: '',
    item: null,
    loadPortfolioData: false,
    loadPortfolios: true,
    onAuthModalShow: () => {},
    onAddItemError: () => {},
    onAddItemSuccess: () => {},
    onAddNoteError: () => {},
    onAddNoteSuccess: () => {},
    onCreateFolderError: () => {},
    onCreateFolderSuccess: () => {},
    onFolderClick: () => {},
    onFolderRemove: () => {},
    onFolderSave: () => {},
    onNewFolderSave: () => {},
    onRemoveItemError: () => {},
    onRemoveItemSuccess: () => {},
    portfolioItemId: '',
    primaryItemPk: '',
    selectedItemIds: [],
    selectedItemImages: [],
    selectedItemTitles: [],
    trackingHooks: {},
    userId: null,
    viewer: {},
};

FolderWrapperWrapper.propTypes = {
    additionalIconClass: PropTypes.string,
    allowSavingMultipleItems: PropTypes.bool,
    authModalTrackingLabel: PropTypes.string,
    closeModal: PropTypes.func,
    currentPortfolioId: PropTypes.string,
    disabledButton: PropTypes.bool,
    iconPosition: PropTypes.string,
    iconText: PropTypes.string,
    item: PropTypes.object,
    loadPortfolioData: PropTypes.bool,
    loadPortfolioItemData: PropTypes.bool,
    loadPortfolios: PropTypes.bool,
    modalHeaderText: PropTypes.string,
    onAuthModalShow: PropTypes.func,
    onAddItemError: PropTypes.func,
    onAddItemSuccess: PropTypes.func,
    onAddNoteError: PropTypes.func,
    onAddNoteSuccess: PropTypes.func,
    onCreateFolderError: PropTypes.func,
    onCreateFolderSuccess: PropTypes.func,
    onFolderClick: PropTypes.func,
    onFolderRemove: PropTypes.func,
    onFolderSave: PropTypes.func,
    onNewFolderSave: PropTypes.func,
    onRemoveItemError: PropTypes.func,
    onRemoveItemSuccess: PropTypes.func,
    portfolioItemId: PropTypes.string,
    primaryItemPk: PropTypes.string,
    relay: PropTypes.object.isRequired,
    selectedItemIds: PropTypes.array,
    selectedItemImages: PropTypes.array,
    selectedItemTitles: PropTypes.array,
    shouldFillInFolder: PropTypes.bool,
    singlePortfolioItem: PropTypes.bool,
    trackingHooks: PropTypes.object,
    viewer: PropTypes.object,
    viewerId: PropTypes.string,
    userId: PropTypes.string,
    userIds: PropTypes.array,
};

export default createFragmentContainer(FolderWrapperWrapper, {
    viewer: graphql`
        fragment FolderWrapperWrapper_viewer on Viewer
            @argumentDefinitions(
                selectedItemIds: { type: "[String]", defaultValue: [] }
                shouldFillInFolder: { type: "Boolean", defaultValue: false }
                userIds: { type: "[String]", defaultValue: [] }
            ) {
            id
            ...FolderWrapper_viewer
                @arguments(
                    selectedItemIds: $selectedItemIds
                    shouldFillInFolder: $shouldFillInFolder
                    userIds: $userIds
                )
        }
    `,
});

export const folderWrapperQuery = graphql`
    query FolderWrapperWrapperQuery(
        $userIds: [String]!
        $selectedItemIds: [String]!
        $shouldFillInFolder: Boolean!
    ) {
        viewer {
            ...FolderWrapperWrapper_viewer
                @arguments(
                    selectedItemIds: $selectedItemIds
                    shouldFillInFolder: $shouldFillInFolder
                    userIds: $userIds
                )
        }
    }
`;
